import { ReactNode } from "react";
import { DateTime } from "luxon";

import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { EnumHelper } from "@/Common/Helpers/EnumHelper";
import { LuxonDateHelper } from "@/Common/Helpers/LuxonDateHelper";
import { PortalRegistrationState } from "@/Common/Models/Portal/Enums/PortalRegistrationState";
import { Button, LinkButton } from "@/DesignSystem";
import { LockedAccountAlert } from "../LockedAccountAlert/LockedAccountAlert";
import { AccountDetailDescription } from "./AccountDetailDescription/AccountDetailDescription";

import styles from "./AccountDetails.module.scss";

interface IAccountDetails {
    status: PortalRegistrationState;
    username: string;
    isPopup?: boolean;
    refusalDate: Date;
    lastLoginDate: Date;
    lastInvitationDate: Date;
    lastLoginAttemptDate: Date;
}

interface AccountDetailsProps extends IAccountDetails {
    onResendInvitation?: () => void;
    onRelink?: () => void;
}

function AccountDetails(props: Readonly<AccountDetailsProps>): JSX.Element {
    const {
        status,
        username,
        lastLoginDate,
        lastInvitationDate,
        lastLoginAttemptDate,
        onResendInvitation,
        onRelink,
        isPopup
    } = props;

    const { getText } = useTranslation();

    const formatDate = (date: Date, includeEmail: boolean = false): ReactNode => {
        if (!DateTime.fromJSDate(date).isValid) {
            return "--";
        }

        let formatted = LuxonDateHelper.formatToFullDateTime(date);

        if (includeEmail)
            formatted += ` ${getText("ToLabel")} ${username}`;

        return formatted;
    };

    const renderStatus = (): ReactNode => (
        <>
            <span className={`${isPopup ? styles.accountStatus : styles.accountStatusBig}`}>
                {getText(`Enums_PortalRegistrationState_${EnumHelper.getName(PortalRegistrationState, status)}`)}
            </span>
            {(status === PortalRegistrationState.RegistrationPending || (status === PortalRegistrationState.InvitationLocked && !isPopup)) && (
                <LinkButton
                    dataCy="resend-invitation"
                    onClick={() => onResendInvitation()}
                    label={getText("PortalInvitation_ResendInvitationLabel")}
                />
            )}
        </>
    );

    const renderInactiveAccount = (): ReactNode => (
        <>
            <AccountDetailDescription label={getText("AccountStatusLabel")} value={renderStatus()} valueColor="red" isPopup={isPopup} />
            <AccountDetailDescription label={getText("PortalInvitation_LastInvitationDateLabel")} value={formatDate(lastInvitationDate, true)} isPopup={isPopup} />
        </>
    );

    const renderActiveAccount = (): ReactNode => (
        <>
            <AccountDetailDescription label={getText("EmailLabel")} value={username} isPopup={isPopup} />
            <AccountDetailDescription label={getText("AccountStatusLabel")} value={renderStatus()} isPopup={isPopup} />
            <AccountDetailDescription label={getText("LastLoginLabel")} value={formatDate(lastLoginDate)} isPopup={isPopup} />
            <AccountDetailDescription label={getText("LastLoginAttemptLabel")} value={formatDate(lastLoginAttemptDate)} isPopup={isPopup} />
        </>
    );

    return (
        <>
            {isPopup ? (
                <h4 className="bottom-border">
                    {getText("PortalInvitation_AccountDetailsLabel")}
                </h4>
            ) :
                (
                    <div className="flex-container heading">
                        <h3 className="thin-font">
                            {getText("PortalInvitation_AccountDetailsLabel")}
                        </h3>
                    </div>
                )
            }

            {(!isPopup && status === PortalRegistrationState.InvitationLocked) && (
                <div className={styles.lockedAccountNote}>
                    <LockedAccountAlert isAlertPopup={!isPopup} />
                </div>
            )}

            <div className={styles.accountDetails}>
                {(status === PortalRegistrationState.RegistrationPending || (status === PortalRegistrationState.InvitationLocked && !isPopup)) && (
                    renderInactiveAccount()
                )}
                {status === PortalRegistrationState.RegistrationComplete && (
                    renderActiveAccount()
                )}
            </div>

            {!isPopup && (
                <div className={styles.updateLinkedProfile}>
                    <Button
                        label={getText("PortalInvitation_UpdateLinkedProfileLabel")}
                        category="secondary"
                        variant="neutral"
                        onClick={() => onRelink()}
                    />
                </div>
            )}
        </>
    );
}

export type { IAccountDetails };

export { AccountDetails };
